// lib/access/masterAgentAccess.ts
//
// The Master agent is not shipped yet. Its four surfaces — Home, New session, Chats, Doctrine —
// are shown as "Coming soon" and cannot be opened, and a user landing on one is sent to the first
// module they actually have access to.
//
// One module, one rule: everything that hides or redirects a Master surface reads from here, so
// turning the agent on later is a single edit rather than a hunt through the portal.

/**
 * Is the Master agent available to this user?
 *
 * FALSE for everyone today. Fater admins keep it so the feature can still be built and reviewed
 * from inside the product — a client never sees it either way. Flip `MASTER_AGENT_ENABLED` to true
 * (or drop the admin condition) when it ships.
 */
const MASTER_AGENT_ENABLED = false;

/**
 * Set NEXT_PUBLIC_MASTER_AGENT_PREVIEW_LOCKED=1 to see the app exactly as a client does, admin
 * bypass included. It only ever makes access MORE restrictive, so it cannot be used to unlock
 * anything — and it is how the locked state gets reviewed without a client login.
 */
const previewLocked = ['1', 'true', 'yes', 'on'].includes(
  String(process.env.NEXT_PUBLIC_MASTER_AGENT_PREVIEW_LOCKED ?? '').toLowerCase(),
);

export const canAccessMasterAgent = (userContext: { isFaterAdmin?: boolean | null } | null | undefined): boolean => {
  if (previewLocked) return false;
  return MASTER_AGENT_ENABLED || Boolean(userContext?.isFaterAdmin);
};

/** The label shown on a locked Master surface. */
export const MASTER_AGENT_COMING_SOON_LABEL = 'Coming soon';

export interface ModuleAccess {
  canAccessDesign: boolean;
  canAccessSource: boolean;
  canAccessAssetManagement: boolean;
  canAccessSlidesTool: boolean;
  /** A Slides deep link is waiting to be applied: land there, whatever the module order says. */
  slidesDeepLink?: boolean;
}

/**
 * Where a user without Master access should land. Ordered by how much of the product each module
 * is: Source is the leasing workflow most users are here for, Studio next, then Analyst, then the
 * Slides pilot. Returns null when the user has no module at all — the caller then leaves them on
 * the current view rather than bouncing them somewhere they cannot use either.
 */
export const resolveLandingView = (access: ModuleAccess): 'projects' | 'tables' | 'slides' | 'source' | null => {
  if (access.slidesDeepLink && access.canAccessSlidesTool) return 'slides';
  if (access.canAccessSource) return 'source';
  if (access.canAccessDesign) return 'projects';
  if (access.canAccessAssetManagement) return 'tables';
  if (access.canAccessSlidesTool) return 'slides';
  return null;
};
